/**
 * Klient HTTP dla publicznego portalu rezerwacyjnego (Faza 9).
 * Nie używa współdzielonego `api` (axios z auth interceptorem) — portal
 * jest publiczny i nie powinien obciążać kolejki refreshu tokenu staffa.
 */

import axios from 'axios';
import type {
  GalleryItemDto,
  PublicAvailabilityResponse,
  PublicBookingCreatedResponse,
  PublicBookingCreateRequest,
  PublicBookingDetail,
  PublicBookingRescheduleRequest,
  PublicEmployeeItem,
  PublicServiceItem,
} from '@crm/shared';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const publicApi = axios.create({
  baseURL: `${baseURL}/public`,
  headers: { 'Content-Type': 'application/json' },
});

export type PublicApiErrorBody = {
  error: { code: string; message: string; details?: unknown };
};

export function publicErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as PublicApiErrorBody | undefined;
    if (data?.error?.message) return data.error.message;
    return err.message || fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function fetchServices(): Promise<PublicServiceItem[]> {
  const res = await publicApi.get<PublicServiceItem[]>('/services');
  return res.data;
}

export async function fetchEmployees(serviceIds: number[]): Promise<PublicEmployeeItem[]> {
  const res = await publicApi.get<PublicEmployeeItem[]>('/employees', {
    params: serviceIds.length > 0 ? { serviceIds: serviceIds.join(',') } : {},
  });
  return res.data;
}

export async function fetchAvailability(args: {
  serviceIds: number[];
  employeeId?: number;
  date: string;
}): Promise<PublicAvailabilityResponse> {
  const res = await publicApi.get<PublicAvailabilityResponse>('/availability', {
    params: {
      serviceIds: args.serviceIds.join(','),
      ...(args.employeeId !== undefined ? { employeeId: args.employeeId } : {}),
      date: args.date,
    },
  });
  return res.data;
}

export async function createBooking(
  body: PublicBookingCreateRequest,
): Promise<PublicBookingCreatedResponse> {
  const res = await publicApi.post<PublicBookingCreatedResponse>('/bookings', body);
  return res.data;
}

export async function fetchBookingByToken(token: string): Promise<PublicBookingDetail> {
  const res = await publicApi.get<PublicBookingDetail>(
    `/bookings/${encodeURIComponent(token)}`,
  );
  return res.data;
}

export async function cancelByToken(
  token: string,
): Promise<{ id: number; cancelledAt: string }> {
  const res = await publicApi.post<{ id: number; cancelledAt: string }>(
    `/bookings/${encodeURIComponent(token)}/cancel`,
  );
  return res.data;
}

export async function rescheduleByToken(
  token: string,
  body: PublicBookingRescheduleRequest,
): Promise<PublicBookingCreatedResponse> {
  const res = await publicApi.post<PublicBookingCreatedResponse>(
    `/bookings/${encodeURIComponent(token)}/reschedule`,
    body,
  );
  return res.data;
}

/**
 * Faza 16 — pobiera publiczną galerię portfolio pracownika.
 * Filtr po `serviceIds` zwraca zdjęcia z tagiem usługi z listy + zdjęcia
 * "ogólne" (bez tagu). Bez auth.
 */
export async function fetchPublicGallery(args: {
  userId: number;
  serviceIds?: number[];
}): Promise<GalleryItemDto[]> {
  const res = await publicApi.get<GalleryItemDto[]>('/gallery', {
    params: {
      userId: args.userId,
      ...(args.serviceIds && args.serviceIds.length > 0
        ? { serviceIds: args.serviceIds.join(',') }
        : {}),
    },
  });
  return res.data;
}
